import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ErrorBanner from '../components/ErrorBanner';
import type {
  AppItem,
  MonitoringCard,
  MonitoringDashboardConfig,
  MonitoringDashboardDetail,
  MonitoringDashboardSummary,
  MonitoringFilters,
  MonitoringQuery,
  MonitoringResponse,
  MonitoringRow
} from '../types';
import { reportError, reportErrorLocal } from '../utils/errors';
import './MonitoringPage.lane38.css';

export default function MonitoringPage({
  apiClient,
  setError,
  isShared
}: {
  apiClient: ApiClient;
  setError: (message: string | null) => void;
  isShared?: boolean;
}) {
  const params = useParams();
  const sharedToken = params.token ?? '';
  const [apps, setApps] = useState<AppItem[]>([]);
  const [appId, setAppId] = useState('');
  const [range, setRange] = useState('24h');
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [data, setData] = useState<Record<string, MonitoringRow[]>>({});
  const [monitorError, setMonitorError] = useState<string | null>(null);
  const [dashboards, setDashboards] = useState<MonitoringDashboardSummary[]>([]);
  const [activeDashboard, setActiveDashboard] = useState<MonitoringDashboardDetail | null>(null);
  const [dashboardName, setDashboardName] = useState('');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [dashboardBusy, setDashboardBusy] = useState(false);
  const [dashboardMessage, setDashboardMessage] = useState<string | null>(null);
  const [includeWarnings, setIncludeWarnings] = useState(true);
  const [includeErrors, setIncludeErrors] = useState(true);
  const [includeDeploy, setIncludeDeploy] = useState(true);
  const [includeSlowest, setIncludeSlowest] = useState(true);
  const [includeSlowestJobs, setIncludeSlowestJobs] = useState(true);
  const [includeCustom, setIncludeCustom] = useState(true);
  const [activeCards, setActiveCards] = useState<MonitoringCard[]>([]);

  const [customBucket, setCustomBucket] = useState('1h');
  const [customAgg, setCustomAgg] = useState('count');
  const [customLevelMin, setCustomLevelMin] = useState('warn');
  const [customKind, setCustomKind] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [customEventName, setCustomEventName] = useState('');
  const [customMetricName, setCustomMetricName] = useState('');
  const [customGroupBy, setCustomGroupBy] = useState<string[]>([]);
  const [customGroupField, setCustomGroupField] = useState('');
  const [customFieldFilterKey, setCustomFieldFilterKey] = useState('');
  const [customFieldFilterValue, setCustomFieldFilterValue] = useState('');
  const [customLimit, setCustomLimit] = useState(50);

  const ranges = useMemo(
    () => [
      { id: '1h', label: 'Last 1 hour', ms: 60 * 60 * 1000 },
      { id: '6h', label: 'Last 6 hours', ms: 6 * 60 * 60 * 1000 },
      { id: '24h', label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000 },
      { id: '7d', label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
      { id: '30d', label: 'Last 30 days', ms: 30 * 24 * 60 * 60 * 1000 }
    ],
    []
  );

  const presets = useMemo<MonitoringCard[]>(
    () => [
      {
        title: 'Warnings and above',
        description: 'Count of warn/error/fatal logs.',
        query: {
          bucket: '1h',
          agg: 'count',
          filters: {
            level_min: 'warn'
          }
        } as MonitoringQuery,
        view: 'timeseries'
      },
      {
        title: 'Errors by message',
        description: 'Top error messages.',
        query: {
          agg: 'count',
          group_by: ['message'],
          limit: 15,
          filters: {
            level: 'error'
          }
        } as MonitoringQuery,
        view: 'bars'
      },
      {
        title: 'Deploy triggers by source',
        description: 'Hook vs poller vs direct.',
        query: {
          bucket: '1h',
          agg: 'count',
          group_by: ['fields.source'],
          filters: {
            event_name: 'deploy.trigger'
          }
        } as MonitoringQuery,
        view: 'timeseries'
      },
      {
        title: 'Slowest requests',
        description: 'Average duration by endpoint.',
        query: {
          agg: 'avg',
          group_by: ['fields.path'],
          limit: 20,
          filters: {
            kind: 'log',
            fields: {
              'method': ['GET', 'POST', 'PATCH', 'DELETE']
            }
          }
        } as MonitoringQuery,
        view: 'bars'
      },
      {
        title: 'Slowest jobs',
        description: 'Average job execution time.',
        query: {
          agg: 'avg',
          group_by: ['fields.status'],
          limit: 10,
          filters: {
            metric_name: 'job.duration_ms'
          }
        } as MonitoringQuery,
        view: 'bars'
      }
    ],
    []
  );

  useEffect(() => {
    apiClient
      .get<AppItem[]>('/api/apps')
      .then((rows) => setApps(rows ?? []))
      .catch((err) => reportError('Failed to load apps', err, setError));
  }, [apiClient, setError]);

  const loadDashboards = useCallback(() => {
    if (isShared) {
      return;
    }
    apiClient
      .get<MonitoringDashboardSummary[]>('/api/monitoring/dashboards')
      .then((rows) => setDashboards(rows ?? []))
      .catch((err) => reportError('Failed to load dashboards', err, setError));
  }, [apiClient, isShared, setError]);

  const loadDashboardDetail = (dashboardId: string) => {
    apiClient
      .get<MonitoringDashboardDetail>(`/api/monitoring/dashboards/${dashboardId}`)
      .then((row) => {
        setActiveDashboard(row);
        setDashboardName(row.name);
        setDashboardDescription(row.description ?? '');
        setRange(row.config.range ?? '24h');
        setActiveCards(row.config.cards ?? []);
        setDashboardMessage(null);
      })
      .catch((err) => reportErrorLocal('Failed to load dashboard', err, setError, setMonitorError));
  };

  const loadSharedDashboard = useCallback(() => {
    if (!sharedToken) {
      return;
    }
    apiClient
      .get<MonitoringDashboardDetail>(`/api/monitoring/dashboards/shared/${sharedToken}`)
      .then((row) => {
        setActiveDashboard(row);
        setDashboardName(row.name);
        setDashboardDescription(row.description ?? '');
        setRange(row.config.range ?? '24h');
        setActiveCards(row.config.cards ?? []);
      })
      .catch((err) =>
        reportErrorLocal('Failed to load shared dashboard', err, setError, setMonitorError)
      );
  }, [apiClient, setError, sharedToken]);

  useEffect(() => {
    if (isShared) {
      loadSharedDashboard();
    } else {
      loadDashboards();
    }
  }, [isShared, loadDashboards, loadSharedDashboard]);

  const resolveRange = useCallback(() => {
    const now = new Date();
    const matched = ranges.find((item) => item.id === range) ?? ranges[2];
    const from = new Date(now.getTime() - matched.ms);
    return { from: from.toISOString(), to: now.toISOString() };
  }, [range, ranges]);

  const buildQuery = useCallback((base: MonitoringQuery): MonitoringQuery => {
    const { from, to } = resolveRange();
    const filters: MonitoringFilters = { ...(base.filters ?? {}) };
    if (appId) {
      filters.app_id = appId;
    }
    return {
      ...base,
      from,
      to,
      filters
    };
  }, [appId, resolveRange]);

  const runCard = useCallback(async (cardId: string, query: MonitoringQuery) => {
    setLoading((prev) => ({ ...prev, [cardId]: true }));
    try {
      const data = await apiClient.post<MonitoringResponse>('/api/monitoring/query', query);
      setData((prev) => ({ ...prev, [cardId]: data.rows ?? [] }));
      setMonitorError(null);
    } catch (err) {
      reportErrorLocal('Failed to load monitoring data', err, setError, setMonitorError);
    } finally {
      setLoading((prev) => ({ ...prev, [cardId]: false }));
    }
  }, [apiClient, setError]);

  const toggleGroupBy = (value: string) => {
    setCustomGroupBy((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const buildCustomCard = useCallback((): MonitoringCard => {
    const filters: MonitoringFilters = {};
    if (customLevelMin) {
      filters.level_min = customLevelMin;
    }
    if (customKind) {
      filters.kind = customKind;
    }
    if (customMessage) {
      filters.message_contains = customMessage;
    }
    if (customEventName) {
      filters.event_name = customEventName;
    }
    if (customMetricName) {
      filters.metric_name = customMetricName;
    }
    if (customFieldFilterKey && customFieldFilterValue) {
      filters.fields = {
        [customFieldFilterKey]: customFieldFilterValue
      };
    }
    const groupBy = [...customGroupBy];
    if (customGroupField) {
      groupBy.push(`fields.${customGroupField}`);
    }
    const query: MonitoringQuery = {
      bucket: customBucket === 'none' ? undefined : customBucket,
      agg: customAgg,
      group_by: groupBy.length ? groupBy : undefined,
      limit: customLimit,
      filters
    };
    return {
      title: 'Custom query',
      description: 'Ad-hoc aggregate from log events.',
      view: groupBy.length || customBucket === 'none' ? 'bars' : 'timeseries',
      query
    };
  }, [
    customAgg,
    customBucket,
    customEventName,
    customFieldFilterKey,
    customFieldFilterValue,
    customGroupBy,
    customGroupField,
    customKind,
    customLevelMin,
    customLimit,
    customMessage,
    customMetricName
  ]);

  const resolveCards = useCallback((): MonitoringCard[] => {
    if (activeCards.length > 0) {
      return activeCards;
    }
    const cards: MonitoringCard[] = [];
    if (includeWarnings) {
      cards.push(presets[0]);
    }
    if (includeErrors) {
      cards.push(presets[1]);
    }
    if (includeDeploy) {
      cards.push(presets[2]);
    }
    if (includeSlowest) {
      cards.push(presets[3]);
    }
    if (includeSlowestJobs) {
      cards.push(presets[4]);
    }
    if (includeCustom) {
      cards.push(buildCustomCard());
    }
    return cards;
  }, [
    activeCards,
    buildCustomCard,
    includeCustom,
    includeDeploy,
    includeErrors,
    includeSlowest,
    includeSlowestJobs,
    includeWarnings,
    presets
  ]);

  const refreshAll = useCallback(() => {
    resolveCards().forEach((card, index) => {
      runCard(`card-${index}`, buildQuery(card.query));
    });
  }, [buildQuery, resolveCards, runCard]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const runCustom = () => {
    const card = buildCustomCard();
    runCard('custom', buildQuery(card.query));
  };

  const saveNewDashboard = async () => {
    if (!dashboardName.trim()) {
      setDashboardMessage('Please provide a dashboard name.');
      return;
    }
    if (dashboardBusy) {
      return;
    }
    setDashboardBusy(true);
    try {
      const payload = {
        name: dashboardName.trim(),
        description: dashboardDescription || null,
        config: {
          range,
          cards: resolveCards()
        }
      };
      const row = await apiClient.post<MonitoringDashboardDetail>(
        '/api/monitoring/dashboards',
        payload
      );
      setActiveDashboard(row);
      setActiveCards(row.config.cards ?? []);
      setDashboardMessage('Dashboard saved.');
      loadDashboards();
    } catch (err) {
      reportErrorLocal('Failed to save dashboard', err, setError, setMonitorError);
    } finally {
      setDashboardBusy(false);
    }
  };

  const updateDashboard = async () => {
    if (!activeDashboard) {
      return;
    }
    if (dashboardBusy) {
      return;
    }
    setDashboardBusy(true);
    try {
      const payload = {
        name: dashboardName.trim(),
        description: dashboardDescription || null,
        config: {
          range,
          cards: resolveCards()
        },
        share_enabled: activeDashboard.share_enabled
      };
      const row = await apiClient.patch<MonitoringDashboardDetail>(
        `/api/monitoring/dashboards/${activeDashboard.id}`,
        payload
      );
      setActiveDashboard(row);
      setActiveCards(row.config.cards ?? []);
      setDashboardMessage('Dashboard updated.');
      loadDashboards();
    } catch (err) {
      reportErrorLocal('Failed to update dashboard', err, setError, setMonitorError);
    } finally {
      setDashboardBusy(false);
    }
  };

  const deleteDashboard = async () => {
    if (!activeDashboard) {
      return;
    }
    if (!confirm(`Delete dashboard "${activeDashboard.name}"?`)) {
      return;
    }
    try {
      await apiClient.del(`/api/monitoring/dashboards/${activeDashboard.id}`);
      setActiveDashboard(null);
      setActiveCards([]);
      setDashboardMessage('Dashboard deleted.');
      loadDashboards();
    } catch (err) {
      reportErrorLocal('Failed to delete dashboard', err, setError, setMonitorError);
    }
  };

  const toggleShare = async (enabled: boolean) => {
    if (!activeDashboard) {
      return;
    }
    if (dashboardBusy) {
      return;
    }
    setDashboardBusy(true);
    try {
      const payload = {
        name: dashboardName.trim() || activeDashboard.name,
        description: dashboardDescription || null,
        config: activeDashboard.config,
        share_enabled: enabled
      };
      const row = await apiClient.patch<MonitoringDashboardDetail>(
        `/api/monitoring/dashboards/${activeDashboard.id}`,
        payload
      );
      setActiveDashboard(row);
      setDashboardMessage(enabled ? 'Sharing enabled.' : 'Sharing disabled.');
      loadDashboards();
    } catch (err) {
      reportErrorLocal('Failed to update sharing', err, setError, setMonitorError);
    } finally {
      setDashboardBusy(false);
    }
  };

  const rotateShare = async () => {
    if (!activeDashboard) {
      return;
    }
    try {
      const row = await apiClient.post<MonitoringDashboardDetail>(
        `/api/monitoring/dashboards/${activeDashboard.id}/share/rotate`,
        {}
      );
      setActiveDashboard(row);
      setDashboardMessage('Share link rotated.');
      loadDashboards();
    } catch (err) {
      reportErrorLocal('Failed to rotate share link', err, setError, setMonitorError);
    }
  };

  const copyShareLink = () => {
    if (!activeDashboard?.share_token) {
      return;
    }
    const link = `${window.location.origin}/monitoring/shared/${activeDashboard.share_token}`;
    navigator.clipboard.writeText(link);
    setDashboardMessage('Share link copied.');
  };

  const cards = resolveCards();

  return (
    <section className="stack monitoring38" aria-label="Monitoring overview">
      <header className="monitoring38-overview">
        <div>
          <p className="monitoring38-kicker">Log analytics</p>
          <h1>Monitoring overview</h1>
          <p>Explore recorded events and aggregates. Missing data is not a health signal.</p>
        </div>
        <div className="monitoring38-context">
          <span>{isShared ? 'Shared dashboard' : 'Dashboard workspace'}</span>
          <span>{cards.length} configured panels</span>
        </div>
      </header>
      {!isShared && (
        <div className="card full">
          <div className="card-header">
            <h2>Dashboards</h2>
            <div className="row">
              <button onClick={loadDashboards}>Refresh</button>
            </div>
          </div>
          {dashboardMessage && <div className="status">{dashboardMessage}</div>}
          <div className="detail-grid">
            <label className="stack">
              Saved dashboards
              <select
                value={activeDashboard?.id ?? ''}
                onChange={(event) => {
                  const id = event.target.value;
                  if (!id) {
                    setActiveDashboard(null);
                    setActiveCards([]);
                    return;
                  }
                  loadDashboardDetail(id);
                }}
              >
                <option value="">New dashboard</option>
                {dashboards.map((dash) => (
                  <option key={dash.id} value={dash.id}>
                    {dash.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="stack">
              Name
              <input value={dashboardName} onChange={(e) => setDashboardName(e.target.value)} />
            </label>
            <label className="stack">
              Description
              <input
                value={dashboardDescription}
                onChange={(e) => setDashboardDescription(e.target.value)}
              />
            </label>
            <div className="row">
              <button onClick={saveNewDashboard} disabled={dashboardBusy}>
                {dashboardBusy ? 'Saving...' : 'Save new'}
              </button>
              {activeDashboard && (
                <>
                  <button className="secondary" onClick={updateDashboard} disabled={dashboardBusy}>
                    Update
                  </button>
                  <button className="secondary" onClick={deleteDashboard}>
                    Delete
                  </button>
                </>
              )}
            </div>
            {activeDashboard && (
              <div className="row">
                <button
                  className="secondary"
                  onClick={() => toggleShare(!activeDashboard.share_enabled)}
                  disabled={dashboardBusy}
                >
                  {activeDashboard.share_enabled ? 'Disable share' : 'Enable share'}
                </button>
                <button className="secondary" onClick={rotateShare} disabled={dashboardBusy}>
                  Rotate link
                </button>
                <button
                  className="ghost"
                  onClick={copyShareLink}
                  disabled={!activeDashboard.share_enabled || !activeDashboard.share_token}
                >
                  Copy link
                </button>
              </div>
            )}
          </div>
          <div className="row wrap monitoring38-inclusions">
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={includeWarnings}
                onChange={() => setIncludeWarnings((prev) => !prev)}
              />
              <span>Include warnings chart</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={includeErrors}
                onChange={() => setIncludeErrors((prev) => !prev)}
              />
              <span>Include errors chart</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={includeDeploy}
                onChange={() => setIncludeDeploy((prev) => !prev)}
              />
              <span>Include deploy triggers chart</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={includeSlowest}
                onChange={() => setIncludeSlowest((prev) => !prev)}
              />
              <span>Include slowest requests chart</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={includeSlowestJobs}
                onChange={() => setIncludeSlowestJobs((prev) => !prev)}
              />
              <span>Include slowest jobs chart</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={includeCustom}
                onChange={() => setIncludeCustom((prev) => !prev)}
              />
              <span>Include custom chart</span>
            </label>
          </div>
        </div>
      )}

      {isShared && activeDashboard && (
        <div className="card full">
          <div className="card-header">
            <h2>Shared dashboard: {activeDashboard.name}</h2>
          </div>
          {activeDashboard.description && <p className="status">{activeDashboard.description}</p>}
        </div>
      )}

      <div className="card full">
        <div className="card-header">
          <h2>Query scope</h2>
          <div className="row">
            <button onClick={refreshAll}>Refresh</button>
          </div>
        </div>
        {monitorError && (
          <ErrorBanner message={monitorError} onDismiss={() => setMonitorError(null)} />
        )}
        <div className="detail-grid">
          <label className="stack">
            App
            <select value={appId} onChange={(event) => setAppId(event.target.value)}>
              <option value="">All apps</option>
              {apps.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </label>
          <label className="stack">
            Range
            <select value={range} onChange={(event) => setRange(event.target.value)}>
              {ranges.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="status monitoring38-scope-note">
          App and range apply to panel queries. Previous results may remain visible during refresh or after a failed query.
        </p>
      </div>

      <div className="monitoring-grid">
        {cards.map((card, index) => {
          const key = `card-${index}`;
          return (
            <div className="card monitoring-card" key={key} aria-busy={Boolean(loading[key])}>
              <div className="card-header">
                <div>
                  <h3>{card.title}</h3>
                  {card.description && <p className="status">{card.description}</p>}
                </div>
                <button
                  className="ghost"
                  onClick={() => runCard(key, buildQuery(card.query))}
                  disabled={loading[key]}
                >
                  {loading[key] ? 'Loading…' : 'Refresh'}
                </button>
              </div>
              <p className="monitoring38-result-meta">
                {loading[key] ? 'Query in progress' : `${(data[key] ?? []).length} rows displayed`}
              </p>
              {card.view === 'timeseries' ? (
                <TimeSeriesChart rows={data[key] ?? []} />
              ) : (
                <BarList rows={data[key] ?? []} />
              )}
            </div>
          );
        })}

        {!isShared && includeCustom && (
          <div className="card monitoring-card monitoring38-custom" aria-busy={Boolean(loading.custom)}>
            <div className="card-header">
              <div>
                <h3>Custom query</h3>
                <p className="status">Build a quick aggregate from log events.</p>
              </div>
              <button className="ghost" onClick={runCustom} disabled={loading.custom}>
                {loading.custom ? 'Loading…' : 'Run'}
              </button>
            </div>
            <div className="detail-grid">
              <label className="stack">
                Bucket
                <select value={customBucket} onChange={(e) => setCustomBucket(e.target.value)}>
                  <option value="none">None</option>
                  <option value="5m">5 minutes</option>
                  <option value="15m">15 minutes</option>
                  <option value="1h">1 hour</option>
                  <option value="1d">1 day</option>
                </select>
              </label>
              <label className="stack">
                Aggregation
                <select value={customAgg} onChange={(e) => setCustomAgg(e.target.value)}>
                  <option value="count">Count</option>
                  <option value="sum">Sum (metric_value)</option>
                  <option value="avg">Average (metric_value)</option>
                  <option value="min">Min (metric_value)</option>
                  <option value="max">Max (metric_value)</option>
                </select>
              </label>
              <label className="stack">
                Level min
                <select value={customLevelMin} onChange={(e) => setCustomLevelMin(e.target.value)}>
                  <option value="">Any</option>
                  <option value="info">Info+</option>
                  <option value="warn">Warn+</option>
                  <option value="error">Error+</option>
                </select>
              </label>
              <label className="stack">
                Kind
                <input value={customKind} onChange={(e) => setCustomKind(e.target.value)} placeholder="log, event, metric" />
              </label>
              <label className="stack">
                Message contains
                <input value={customMessage} onChange={(e) => setCustomMessage(e.target.value)} />
              </label>
              <label className="stack">
                Event name
                <input value={customEventName} onChange={(e) => setCustomEventName(e.target.value)} />
              </label>
              <label className="stack">
                Metric name
                <input value={customMetricName} onChange={(e) => setCustomMetricName(e.target.value)} />
              </label>
              <label className="stack">
                Group by field key
                <input value={customGroupField} onChange={(e) => setCustomGroupField(e.target.value)} placeholder="source" />
              </label>
              <label className="stack">
                Field filter key
                <input value={customFieldFilterKey} onChange={(e) => setCustomFieldFilterKey(e.target.value)} placeholder="source" />
              </label>
              <label className="stack">
                Field filter value
                <input value={customFieldFilterValue} onChange={(e) => setCustomFieldFilterValue(e.target.value)} placeholder="hook" />
              </label>
              <label className="stack">
                Limit
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={customLimit}
                  onChange={(e) => setCustomLimit(Number(e.target.value))}
                />
              </label>
            </div>
            <div className="stack">
              <span className="status">Group by</span>
              <div className="row wrap">
                {['level', 'kind', 'app_id', 'service', 'event_name', 'metric_name', 'message'].map((item) => (
                  <label key={item} className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={customGroupBy.includes(item)}
                      onChange={() => toggleGroupBy(item)}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="stack">
              <TimeSeriesChart rows={data.custom ?? []} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function formatGroupLabel(group: Record<string, string | null>) {
  const entries = Object.entries(group || {});
  if (entries.length === 0) {
    return 'total';
  }
  return entries
    .map(([key, value]) => `${key}=${value ?? '—'}`)
    .join(' · ');
}

export function TimeSeriesChart({ rows }: { rows: MonitoringRow[] }) {
  const buckets = Array.from(
    new Set(rows.map((row) => row.bucket).filter((value): value is string => Boolean(value)))
  ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  if (buckets.length === 0) {
    return <BarList rows={rows} />;
  }
  const bucketIndex = new Map(buckets.map((bucket, idx) => [bucket, idx]));
  const seriesMap = new Map<string, number[]>();
  for (const row of rows) {
    if (!row.bucket) {
      continue;
    }
    const idx = bucketIndex.get(row.bucket);
    if (idx === undefined) {
      continue;
    }
    const label = formatGroupLabel(row.group || {});
    const values = seriesMap.get(label) ?? Array(buckets.length).fill(0);
    values[idx] = row.value;
    seriesMap.set(label, values);
  }
  const series = Array.from(seriesMap.entries());
  const allValues = series.flatMap((entry) => entry[1]);
  const maxValue = Math.max(1, ...allValues);
  const width = 600;
  const height = 140;
  const padding = 16;
  const colors = ['#1f6feb', '#f97316', '#10b981', '#e11d48', '#a855f7'];
  return (
    <div className="chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Aggregate values over time; series labels below">
        {series.map(([label, values], idx) => {
          const points = values.map((value, i) => {
            const x = padding + (i * (width - padding * 2)) / Math.max(1, values.length - 1);
            const y = height - padding - (value / maxValue) * (height - padding * 2);
            return `${x},${y}`;
          });
          return (
            <polyline
              key={label}
              points={points.join(' ')}
              fill="none"
              stroke={colors[idx % colors.length]}
              strokeWidth="2"
            />
          );
        })}
      </svg>
      <div className="chart-legend">
        {series.map(([label], idx) => (
          <span key={label} className="legend-item">
            <span
              className="legend-swatch"
              style={{ background: colors[idx % colors.length] }}
            />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function BarList({ rows }: { rows: MonitoringRow[] }) {
  if (!rows || rows.length === 0) {
    return <p className="status monitoring38-empty">No rows to display. Check the query scope or refresh results.</p>;
  }
  const sorted = [...rows].sort((a, b) => b.value - a.value);
  const maxValue = Math.max(1, ...sorted.map((row) => row.value));
  return (
    <div className="bar-list">
      {sorted.map((row, idx) => {
        const label = formatGroupLabel(row.group || {});
        const width = Math.round((row.value / maxValue) * 100);
        return (
          <div key={`${label}-${idx}`} className="bar-row">
            <span className="mono">{label}</span>
            <div className="bar-track">
              <div className="bar-fill" style={{ width: `${width}%` }} />
            </div>
            <span className="mono">{row.value.toFixed(0)}</span>
          </div>
        );
      })}
    </div>
  );
}
